import PropTypes from 'prop-types';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';

// material-ui
import { makeStyles } from '@material-ui/styles';
import { Avatar, Box, Chip, ListItemIcon, ListItemText, Typography, useMediaQuery } from '@material-ui/core';
import ListItemButton from '@material-ui/core/ListItemButton';

// project imports
import { SET_MENU } from '../../../../../store/actions';

// assets
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';

// style constant
const useStyles = makeStyles((theme) => ({
    listIcon: {
        minWidth: '18px',
        marginTop: 'auto',
        marginBottom: 'auto'
    },
    listCustomIconSub: {
        width: '6px',
        height: '6px'
    },
    listCustomIconSubActive: {
        width: '8px',
        height: '8px'
    },
    listItem: {
        marginBottom: '5px',
        alignItems: 'center'
    },
    listItemNoBack: {
        marginBottom: '5px',
        backgroundColor: 'transparent !important',
        paddingTop: '8px',
        paddingBottom: '8px',
        alignItems: 'flex-start'
    },
    subMenuCaption: {
        ...theme.typography.subMenuCaption
    }
}));

//-----------------------|| SIDEBAR MENU LIST ITEMS ||-----------------------//

const NavItem = ({ item, level }) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const location = useLocation();
    const matchesSM = useMediaQuery((theme) => theme.breakpoints.down('md'));

    const Icon = item.icon;
    // URL-driven, like the sibling NavCollapse (see its hasActiveDescendant) — NavItem doesn't
    // remount on route changes, so deriving "active" from click history (the old
    // customization.isOpen Redux value, set only by this item's own onClick) went stale after
    // browser back/forward or any navigation that didn't originate from clicking this item.
    const isActive = !!item.url && (location.pathname === item.url || location.pathname.startsWith(`${item.url}/`));

    const itemIcon = item.icon ? (
        <Box
            sx={{
                width: 30,
                height: 30,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: isActive ? 'rgba(25,118,210,0.1)' : 'transparent',
                transition: 'background-color 0.15s ease',
            }}
        >
            <Icon
                stroke={1.5}
                size="1.35rem"
                style={{
                    color: isActive ? '#1976d2' : 'rgba(58, 74, 92, 0.55)',
                    transition: 'color 0.15s ease',
                }}
            />
        </Box>
    ) : (
        <Box
            sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: isActive ? '#1976d2' : 'transparent',
            }}
        />
    );

    let itemTarget = '';
    if (item.target) {
        itemTarget = '_blank';
    }

    const LinkComponent = React.useMemo(
        () => React.forwardRef((props, ref) => <Link ref={ref} {...props} to={item.url} />),
        [item.url]
    );
    let listItemProps = { component: LinkComponent };
    if (item.external) {
        listItemProps = { component: 'a', href: item.url };
    }

    const itemHandler = () => {
        matchesSM && dispatch({ type: SET_MENU, opened: false });
    };

    return (
        <ListItemButton
            {...listItemProps}
            disabled={item.disabled}
            selected={isActive}
            onClick={itemHandler}
            target={itemTarget}
            className={level > 1 ? classes.listItemNoBack : classes.listItem}
            sx={{
                borderRadius: 2,
                mb: '2px',
                mx: 0.5,
                py: 0.75,
                color: isActive ? '#1976d2' : '#3a4a5c',
                backgroundColor: isActive ? 'rgba(25,118,210,0.08)' : 'transparent',
                transition: 'background-color 0.15s ease',
                '&:hover': {
                    backgroundColor: isActive
                        ? 'rgba(25,118,210,0.12)'
                        : 'rgba(0,0,0,0.03)',
                },
            }}
            style={{ paddingLeft: level * 23 + 12 + 'px' }}
        >
            <ListItemIcon sx={{ minWidth: 26, color: 'inherit' }}>{itemIcon}</ListItemIcon>
            <ListItemText
                primary={
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: isActive ? 600 : 400,
                            fontSize: '0.82rem',
                            color: 'inherit',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {item.title}
                    </Typography>
                }
                secondary={
                    item.caption && (
                        <Typography variant="caption" className={classes.subMenuCaption} display="block" gutterBottom>
                            {item.caption}
                        </Typography>
                    )
                }
            />
            {item.chip && (
                <Chip
                    color={item.chip.color}
                    variant={item.chip.variant}
                    size={item.chip.size}
                    label={item.chip.label}
                    avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
                />
            )}
        </ListItemButton>
    );
};

NavItem.propTypes = {
    item: PropTypes.object,
    level: PropTypes.number
};

export default NavItem;
